import { DownOutlined, UserOutlined } from '@ant-design/icons';
import { Layout, Row, Col, Dropdown, Menu, Button, Form, Input, Modal } from 'antd';
import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { usePromise } from 'react-use';

import { Spinner } from '../../components/Spinner';
import { authService } from '../../services/auth';
import { useEffectTrigger } from '../../utils/hooks/useEffectTrigger';
import { openSuccessNotification } from '../../utils/notifications';
import { showApiErrors } from '../../utils/showApiErrors';
import moduleStyles from '../PasswordChangeModal.module.css';

const UserMenu = ({
  userFullName,
  userHasPassword,
  onPasswordChange
}: {
  userFullName: string;
  userHasPassword: boolean;
  onPasswordChange: () => void;
}) => {
  const [loading, setLoading] = useState(false);

  const mounted = usePromise();
  const onLogout = async (): Promise<void> => {
    setLoading(true);

    try {
      await mounted(authService.logout()); // Returns 'Warning: Can't perform a React state update on an unmounted component...' without mounted
    } catch (e) {
      showApiErrors(e as any);
    } finally {
      setLoading(false);
    }
  };

  const menu = (
    <Menu>
      <Menu.Item>
        {/* <LogoutOutlined /> */}
        {loading ? (
          <Spinner size="small" height="32px" />
        ) : (
          <div
            onClick={() => {
              onLogout();
            }}
          >
            Logout
          </div>
        )}
      </Menu.Item>
      {userHasPassword && (
        <Menu.Item>
          <div onClick={() => onPasswordChange()}>Change Password</div>
        </Menu.Item>
      )}
    </Menu>
  );

  return (
    <Dropdown overlay={menu} trigger={['click']}>
      <div
        style={{
          float: 'right',
          marginRight: 20,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}
      >
        <UserOutlined style={{ fontSize: '16px', color: '#6b7280' }} />
        <span style={{ fontSize: '14px', fontWeight: 500, color: '#374151' }}>{userFullName}</span>
        <DownOutlined style={{ fontSize: '12px', color: '#9ca3af' }} />
      </div>
    </Dropdown>
  );
};

const HeaderUserInfo = ({ onPasswordChange }: { onPasswordChange: () => void }) => {
  const [userFullName, setUserFullName] = useState('');
  const [userHasPassword, setUserHasPassword] = useState(false);
  const [userInfoRefreshTrigger, activateUserInfoRefreshTrigger] = useEffectTrigger();

  useEffect(() => {
    const { firstName, lastName, hasPassword } = authService.getUserInfo();
    setUserFullName(`${firstName} ${lastName}`);
    setUserHasPassword(hasPassword);
  }, [userInfoRefreshTrigger]);

  useEffect(() => {
    authService.registerSubscriber(activateUserInfoRefreshTrigger);
    return () => authService.unregisterSubscriber(activateUserInfoRefreshTrigger);
  }, [activateUserInfoRefreshTrigger]);

  return <UserMenu onPasswordChange={onPasswordChange} userFullName={userFullName} userHasPassword={userHasPassword} />;
};

export const Header = (): JSX.Element => {
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [form] = Form.useForm();

  const onSuccess = () => {
    form.resetFields();
    setIsPasswordModalOpen(false);
    openSuccessNotification({ message: 'Password successfully changed' });
  };

  const handleSubmit = async (values: {
    newPassword: string;
    newPasswordConfirmation: string;
    oldPassword: string;
  }): Promise<void> => {
    const { newPassword, oldPassword } = values;
    try {
      setIsLoading(true);
      await authService.changeUserPassword({
        oldPassword,
        newPassword
      });
      onSuccess();
    } catch (e) {
      showApiErrors(e as AxiosError);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Layout.Header style={{ background: '#fff', padding: 0 }}>
        <Row style={{ flexWrap: 'nowrap', width: '100%' }}>
          <Col style={{ width: '100%', marginRight: '15px' }} />
          <Col style={{ width: '300px' }}>
            <HeaderUserInfo onPasswordChange={() => setIsPasswordModalOpen(true)} />
          </Col>
        </Row>
      </Layout.Header>
      <Modal
        footer={null}
        open={isPasswordModalOpen}
        onCancel={() => {
          form.resetFields();
          setIsPasswordModalOpen(false);
        }}
      >
        <Form form={form} onFinish={(values) => void handleSubmit(values)}>
          <h3>Change Password</h3>
          <p className={moduleStyles.passwordLabel}>Old password</p>
          <Form.Item name="oldPassword" rules={[{ required: true, message: 'Please enter old password' }]}>
            <Input className={moduleStyles.passwordInput} type="password" placeholder="Old password" />
          </Form.Item>
          <p className={moduleStyles.passwordLabel}>New password</p>
          <Form.Item name="newPassword" rules={[{ required: true, message: 'Please enter new password' }]}>
            <Input className={moduleStyles.passwordInput} type="password" placeholder="New password" />
          </Form.Item>

          <p className={moduleStyles.passwordLabel}>Confirm new password</p>
          <Form.Item
            name="newPasswordConfirmation"
            rules={[
              { required: true, message: 'Please confirm your password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }

                  return Promise.reject(new Error('The two passwords do not match!'));
                }
              })
            ]}
          >
            <Input className={moduleStyles.passwordInput} type="password" placeholder="Confirm new password" />
          </Form.Item>
          <div>
            <Button type="primary" style={{ marginTop: '10px' }} htmlType="submit" loading={isLoading}>
              Confirm
            </Button>
          </div>
        </Form>
      </Modal>
    </>
  );
};
